import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { AnalysisResponse, WorkflowStatus } from '../types/workflow';
import { 
  Module4Service, 
  useGetBatchStatusDb, 
  useGetModuleConfigDb, 
  useGetBatchStatusByWorkflowDb,
  DbBatchProcessResponse
} from '../services/module4Service';
import { parseJobIdFromRoute } from '../services/routingAdapter';
import WorkflowStepper from '@/components/WorkflowStepper';
import { getWorkflowStatus } from '@/utils/moduleStatus';
import blobService from '../services/storage/blobService';
import KeywordResultsVisualization from '../components/module5/KeywordResultsVisualization';
import { useDatabaseMode } from '../context/DatabaseModeContext';

import ConfigurationDetailsPanel from '../components/module5/ConfigurationDetailsPanel';
import JobStatusPanel from '../components/module5/JobStatusPanel';
import BatchJobProgressBar from '../components/module5/BatchJobProgressBar';
import { useApp } from '../context/AppContext';
import { useWorkflowContext } from '../context/WorkflowContext';
import { Module1Service } from '../services/module1Service';
import { clientService } from '../services/api/clientService';
import { Box, Typography, Paper, Grid, Alert, CircularProgress, Button as MuiButton, Breadcrumbs, Link } from '@mui/material';
import { Download as DownloadIcon, Home as HomeIcon, ArrowBack as ArrowBackIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';

// Import our extracted utilities
import { shouldThrottleRequest } from '../components/module5/utils/throttleUtils';
import { useSmartPolling, isJobCompleted } from '../components/module5/hooks/useSmartPolling';
import { fetchBatchStatus, fetchModuleStatus } from '../components/module5/utils/fetchUtils';
import { 
  ExtendedAnalysisResponse, 
  mapDbStatusToExtendedResponse 
} from '../components/module5/utils/statusMappingUtils';

// Import new UX improvement components
import JobStatusTransition from '../components/module5/JobStatusTransition';

// Helper to extract filename from URL
const getFilenameFromUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    // Split pathname and get the last part
    const pathParts = parsedUrl.pathname.split('/');
    const filename = pathParts[pathParts.length - 1];
    
    // If we got a filename, return it (preserving its extension)
    if (filename) {
      return filename;
    }
    
    // Fallback: check if URL contains 'results.json' or similar
    if (url.includes('results.json')) {
      return 'results.json';
    }
    
    // Final fallback
    return 'download.json';
  } catch (e) {
    // Fallback for potentially malformed URLs or paths
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];
    
    if (lastPart) {
      return lastPart;
    }
    
    // Check if URL contains 'results.json'
    if (url.includes('results.json')) {
      return 'results.json';
    }
    
    return 'download.json';
  }
};

// Convert blob storage URL to API download URL to avoid CORS issues
const convertToApiDownloadUrl = (blobUrl: string): string => {
  try {
    // Since CORS is now enabled on Azure Blob Storage, we can use the direct URL
    // Just return the original blob URL
    return blobUrl;
  } catch {
    // If anything fails, return the original URL
    return blobUrl;
  }
};

const Module5JobMonitoring = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ExtendedAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLongRunning, setIsLongRunning] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [clientData, setClientData] = useState<any>(null);

  const [hasTriedSnowflakeSave, setHasTriedSnowflakeSave] = useState(false);
  const { serviceStatus } = useApp();
  const { isUsingDatabaseApproach } = useDatabaseMode();
  const { triggerRefresh } = useWorkflowContext();
  
  // Extract workflowId and moduleId from query parameters instead of path parameters
  const workflowIdStr = searchParams.get('workflowId');
  const moduleIdStr = searchParams.get('moduleId');
  const clientIdStr = searchParams.get('clientId'); // Keep for compatibility, though not present in example URL
  
  // Parse job ID from URL parameter with fallback mechanisms
  const parsedJobId = parseJobIdFromRoute(jobId);
  
  // Check localStorage for last job ID in case route param is lost or malformed
  const lastStartedJobId = localStorage.getItem('last_started_job_id');
  const lastStartedJobType = localStorage.getItem('last_started_job_type');
  

  
  // Initialize activeModuleId with moduleId from query params if available
  const [activeModuleId, setActiveModuleId] = useState<number | null>(() => {
    // Priority 0: Use moduleId from query params if available
    if (moduleIdStr) {
      const parsedModuleId = parseInt(moduleIdStr, 10);
      if (!isNaN(parsedModuleId)) {
        return parsedModuleId;
      }
    }
    
    // Priority 1: Use numericId directly from the parsed URL if available (e.g., /jobs/db-123)
    if (parsedJobId.numericId) {
      return parsedJobId.numericId;
    }

    // Priority 2: If last job was 'db' type and its stored ID is NOT a UUID, parse it.
    const looksLikeUuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (lastStartedJobType === 'db' && lastStartedJobId && !looksLikeUuidPattern.test(lastStartedJobId)) {
      const parsedNumericId = parseInt(lastStartedJobId, 10);
      if (!isNaN(parsedNumericId)) {
        return parsedNumericId;
      }
    }
    
    return null;
  });
  
  // Add state for workflow ID from query params
  const [workflowId, setWorkflowId] = useState<number | null>(() => {
    if (workflowIdStr) {
      const parsedWorkflowId = parseInt(workflowIdStr, 10);
      if (!isNaN(parsedWorkflowId)) {
        return parsedWorkflowId;
      }
    }
    return null;
  });
  
  // Reference to track current job status for polling logic
  const currentStatusRef = useRef<string | undefined>(undefined);
  // Reference for last known data
  const lastStatusDataRef = useRef<any>(null);
  // Reference to track if job is in a terminal state
  const isCompletedRef = useRef<boolean>(false);
  // Reference for tracking previous jobId outside the effect
  const prevJobIdRef = useRef<string | null>(null);

  // Get polling interval from our custom hook
  const pollingInterval = useSmartPolling(currentStatusRef.current);
  
  // Database-first React Query hooks with optimized polling
  const batchStatusDbQuery = useGetBatchStatusByWorkflowDb(
    parsedJobId.numericId || 0, 
    undefined, // clientId
    { 
      enabled: isUsingDatabaseApproach && parsedJobId.numericId !== null && !isCompletedRef.current,
      refetchInterval: () => {
        // Stop polling if job is completed - check the current status
        if (currentStatusRef.current && isJobCompleted(currentStatusRef.current)) {
          return false;
        }
        return pollingInterval;
      },
      staleTime: 5000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      queryFn: () => fetchBatchStatus(parsedJobId.numericId || 0, undefined, lastStatusDataRef.current)
    }
  );
  
  // Fallback to deprecated moduleId-based query only when necessary
  const legacyBatchStatusDbQuery = useGetBatchStatusDb(
    activeModuleId !== null ? activeModuleId : 0,
    {
      enabled: isUsingDatabaseApproach && !parsedJobId.numericId && activeModuleId !== null && !isCompletedRef.current,
      refetchInterval: () => {
        // Stop polling if job is completed - check the current status
        if (currentStatusRef.current && isJobCompleted(currentStatusRef.current)) {
          return false;
        }
        return pollingInterval;
      },
      staleTime: 5000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      queryFn: () => fetchModuleStatus(activeModuleId !== null ? activeModuleId : 0, lastStatusDataRef.current)
    }
  );

  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (clientIdStr && /^\d+$/.test(clientIdStr)) {
        try {
          const client = await clientService.getClient(parseInt(clientIdStr, 10));
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };

    fetchClientData();
  }, [clientIdStr]);

  // Add success handler effects for both queries
  useEffect(() => {
    if (batchStatusDbQuery.data) {
      if (isJobCompleted(batchStatusDbQuery.data.status)) {
        isCompletedRef.current = true;
      }
    }
  }, [batchStatusDbQuery.data]);

  useEffect(() => {
    if (legacyBatchStatusDbQuery.data) {
      if (isJobCompleted(legacyBatchStatusDbQuery.data.status)) {
        isCompletedRef.current = true;
      }
    }
  }, [legacyBatchStatusDbQuery.data]);
  
  const moduleConfigQuery = useGetModuleConfigDb(
    activeModuleId || parsedJobId.numericId || 0,
    {
      enabled: isUsingDatabaseApproach && (activeModuleId !== null || parsedJobId.numericId !== null),
      staleTime: 60000,
      refetchOnWindowFocus: false,
      retry: false, // Don't retry on 404/500 errors to avoid spam
      retryOnMount: false
    }
  );
  
  // Use the appropriate query result based on available IDs
  const activeStatusQuery = parsedJobId.numericId ? batchStatusDbQuery : legacyBatchStatusDbQuery;
  

  
  // Update status ref and lastStatusDataRef whenever query data changes
  useEffect(() => {
    if (activeStatusQuery.data) {
      currentStatusRef.current = activeStatusQuery.data.status;
      lastStatusDataRef.current = activeStatusQuery.data;
    }
  }, [activeStatusQuery.data]);
  
  // Also update status ref from manual polling
  useEffect(() => {
    if (status?.status) {
      currentStatusRef.current = status.status;
      // Also update lastStatusDataRef if this is from manual polling
      if (!isUsingDatabaseApproach) {
        lastStatusDataRef.current = status;
      }
    }
  }, [status, isUsingDatabaseApproach]);
  
  // Add this to check completion state when status changes
  const prevStatusRef = useRef<string | undefined>();
  useEffect(() => {
    if (isJobCompleted(status?.status)) {
      isCompletedRef.current = true;
      setLoading(false); // Ensure loading is stopped
      
      // Only trigger refresh if status actually changed to completed (prevent infinite loop)
      if (triggerRefresh && status?.status !== prevStatusRef.current && isJobCompleted(status?.status)) {
        triggerRefresh();
      }
    }
    prevStatusRef.current = status?.status;
  }, [status?.status]); // Don't include triggerRefresh to avoid dependency issues
  
  // Force UI update when completion is detected from React Query but status might lag
  useEffect(() => {
    if (isUsingDatabaseApproach && (batchStatusDbQuery.data || legacyBatchStatusDbQuery.data)) {
      const queryData = batchStatusDbQuery.data || legacyBatchStatusDbQuery.data;
      if (queryData && isJobCompleted(queryData.status)) {
        // Ensure UI status is updated even if main effect hasn't run yet
        if (status?.status !== queryData.status) {
          const mappedStatus = mapDbStatusToExtendedResponse(queryData, jobId);
          setStatus(mappedStatus);
        }
        setLoading(false);
        isCompletedRef.current = true;
      }
    }
  }, [batchStatusDbQuery.data, legacyBatchStatusDbQuery.data, isUsingDatabaseApproach, status?.status, jobId]);

  // Effect to handle database-first status updates
  useEffect(() => {
    // Only proceed if we're using database approach and have status data
    if (isUsingDatabaseApproach && activeStatusQuery.data) {
      const statusData = activeStatusQuery.data;
      
      // Map DB status to local status format for UI compatibility using our utility
      const mappedStatus = mapDbStatusToExtendedResponse(statusData, jobId);
      
      // Force completion flag if status indicates completion
      if (isJobCompleted(statusData.status)) {
        isCompletedRef.current = true;
      }
      
      setStatus(mappedStatus);
      setLoading(false);
    }
  }, [isUsingDatabaseApproach, activeStatusQuery.data, jobId]);

  // The main polling effect with full implementation (critical for fixing loading state)
  useEffect(() => {
    let timeoutId: number | undefined;
    let pollCount = 0;
    const MAX_POLLS = 30; 
    const ABSOLUTE_MAX_POLLS = 240; // ~40 minutes with 10s intervals
    const INITIAL_INTERVAL = 10000; 
    const MAX_INTERVAL = 60000; 

    // Only reset completion flag when jobId changes, not for other dependency changes
    if (jobId) {
      if (prevJobIdRef.current !== jobId) {
        isCompletedRef.current = false;
        prevJobIdRef.current = jobId;
        setIsLongRunning(false);
        setPollAttempts(0);
      }
    }
    
    // Early exit if job is already completed
    if (isCompletedRef.current) {
      setLoading(false);
      return;
    }

    const pollStatus = async () => {
      if (!jobId) return;
      
      // Skip polling if job is already complete
      if (isCompletedRef.current) {
        setLoading(false);
        return;
      }
      
      // Double-check current status before making API call
      if (status?.status && isJobCompleted(status.status)) {
        isCompletedRef.current = true;
        setLoading(false);
        return;
      }

      // Skip manual polling if React Query is already fetching
      if (isUsingDatabaseApproach && activeModuleId && (batchStatusDbQuery.isFetching || legacyBatchStatusDbQuery.isFetching)) {
        return;
      }
      if (isUsingDatabaseApproach && parsedJobId.isDatabase && parsedJobId.numericId && batchStatusDbQuery.isFetching) {
        return;
      }
      
      if (parsedJobId.isDatabase && !activeModuleId && parsedJobId.numericId) {
        setActiveModuleId(parsedJobId.numericId);
        return; 
      }
      
      pollCount++;
      setPollAttempts(pollCount);
      const baseInterval = Math.min(INITIAL_INTERVAL * Math.pow(1.5, pollCount - 1), MAX_INTERVAL);
      const interval = baseInterval + (Math.random() * 2000);

      if (pollCount >= ABSOLUTE_MAX_POLLS) {
        setIsLongRunning(false);
        isCompletedRef.current = true; // Mark as completed to prevent further polling
        setError('Job monitoring timed out after 40 minutes. The job may still be processing.');
        setLoading(false);
        // Stop polling - job has exceeded absolute maximum time
        return;
      }
      
      if (pollCount >= MAX_POLLS) {
        setIsLongRunning(true);
        // Continue polling but with a longer interval
        const extendedInterval = MAX_INTERVAL + (Math.random() * 5000);
        timeoutId = window.setTimeout(pollStatus, extendedInterval);
        return;
      }
      
      // Handle temp-prefixed jobs
      if (jobId.startsWith('temp-')) {
        const match = jobId.match(/temp-(\d+)/);
        if (match && match[1]) {
          const timestamp = match[1];
          const storedWorkflowId = localStorage.getItem(`temp_job_${timestamp}_workflow_id`);
          if (storedWorkflowId) {
            try {
              const workflowIdNum = parseInt(storedWorkflowId, 10);
              if (isNaN(workflowIdNum)) {
                setLoading(false); // Important: set loading to false on error
                return;
              }
              const module4Service = Module4Service.getInstance();
              const response: DbBatchProcessResponse = await module4Service.getBatchStatusByWorkflowDb(workflowIdNum);
              const mappedStatus = mapDbStatusToExtendedResponse(response, jobId);
              
              setStatus(mappedStatus);
              setLoading(false);
              
              if (!isJobCompleted(response.status)) {
                timeoutId = window.setTimeout(pollStatus, interval);
              } else {
                isCompletedRef.current = true;
              }
              return;
            } catch (err) {
              setLoading(false); // Important: set loading to false on error
            }
          }
        }
      }

      // Apply throttling
      const requestKey = `manualPoll-${jobId}`;
      if (shouldThrottleRequest(requestKey)) {
        timeoutId = window.setTimeout(pollStatus, 5000);
        return;
      }

      try {
        const looksLikeUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(jobId);

        // Handle UUID jobs
        if (looksLikeUuid) {
          const moduleIdFromQuery = searchParams.get('moduleId');
          const workflowIdFromQuery = searchParams.get('workflowId');

          let targetModuleId: number | null = null;
          let targetWorkflowId: number | null = null;

          if (moduleIdFromQuery) {
            const parsed = parseInt(moduleIdFromQuery, 10);
            if (!isNaN(parsed)) targetModuleId = parsed;
          }
          if (workflowIdFromQuery) {
            const parsed = parseInt(workflowIdFromQuery, 10);
            if (!isNaN(parsed)) targetWorkflowId = parsed;
          }

          const m4service = Module4Service.getInstance();
          let response: DbBatchProcessResponse | undefined;

          if (targetModuleId) {
            response = await m4service.getBatchStatusDb(targetModuleId);
          } else if (targetWorkflowId) {
            response = await m4service.getBatchStatusByWorkflowDb(targetWorkflowId);
          }

          if (response) {
            const mappedStatus = mapDbStatusToExtendedResponse(response, jobId);

            setStatus(mappedStatus);
            setLoading(false);
            
            if (!isJobCompleted(response.status)) {
              timeoutId = window.setTimeout(pollStatus, interval);
            } else {
              isCompletedRef.current = true;
            }
            return;
          }
        }
        
        // Handle batch- or m4- prefixed jobs
        if (jobId.startsWith('batch-') || jobId.startsWith('m4-')) {
          const module4Service = Module4Service.getInstance();
          const response = await module4Service.getJobStatus(jobId);
          setStatus(response as unknown as ExtendedAnalysisResponse);
          setLoading(false);
          
          if (response.status !== 'completed' && response.status !== 'failed') {
            timeoutId = window.setTimeout(pollStatus, interval);
          } else {
            isCompletedRef.current = true;
          }
          return;
        }
        
        // Handle DB-prefixed or activeModuleId based polling as fallback
        if (parsedJobId.isDatabase && parsedJobId.numericId && !activeModuleId) {
            const m4service = Module4Service.getInstance();
            const response: DbBatchProcessResponse = await m4service.getBatchStatusDb(parsedJobId.numericId);
            const mappedStatus = mapDbStatusToExtendedResponse(response, jobId);
            
            setStatus(mappedStatus);
            setLoading(false);
            
            if (!isJobCompleted(response.status)) {
              timeoutId = window.setTimeout(pollStatus, interval);
            } else {
              isCompletedRef.current = true;
            }
            return;
        }
        
        // Final fallback for Module 1 or other job types
        const module1Service = Module1Service.getInstance();
        const response = await module1Service.getAnalysisStatus(jobId);
        setStatus(response as unknown as ExtendedAnalysisResponse);
        setLoading(false);
        
        if (!isJobCompleted(response.status)) {
          timeoutId = window.setTimeout(pollStatus, interval);
        } else {
          isCompletedRef.current = true;
        }
      } catch (err) {
        setError('Failed to fetch job status');
        setLoading(false); // Important: set loading to false on error
      }
    };

    // Initial poll to start the process
    pollStatus();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [jobId]); // Reduced dependencies - only restart polling when jobId changes



  const [isStepperCollapsed, setIsStepperCollapsed] = useState(false);

  // Add handler for stepper collapse
  const handleStepperCollapseChange = (collapsed: boolean) => {
    setIsStepperCollapsed(collapsed);
  };

  const [keywordsJsonContent, setKeywordsJsonContent] = useState<string | null>(null);
  const [isLoadingJson, setIsLoadingJson] = useState(false);
  const [fileTooLarge, setFileTooLarge] = useState(false);
  const [keywordCount, setKeywordCount] = useState<number | null>(null);
  const MAX_KEYWORDS_FOR_VISUALIZATION = 10000; // Maximum keywords to load for visualization
  
  // Snowflake save is now handled by backend automatically
  const isSavingToSnowflake = false;
  const snowflakeSaveSuccess = false;
  const snowflakeSaveError = null;

  // Manual refresh function
  const handleManualRefresh = () => {
    if (isUsingDatabaseApproach) {
      if (parsedJobId.numericId) {
        batchStatusDbQuery.refetch();
      } else if (activeModuleId) {
        legacyBatchStatusDbQuery.refetch();
      }
    }
    // For non-database approach, the polling will handle it automatically
  };

  // Add effect to fetch categorized keywords content when available
  useEffect(() => {
    // Early return if status is not loaded yet
    if (!status) {
      return;
    }
    
    // Look for JSON results first, fall back to CSV if needed
    const resultsUrl = status?.output_links?.['results.json'] || status?.output_links?.categorized_keywords;
    
    if (resultsUrl && status.status === 'completed') {
      // Check if we have too many keywords for visualization
      const totalKeywords = status?.total_items || 0;
      setKeywordCount(totalKeywords);
      
      if (totalKeywords > MAX_KEYWORDS_FOR_VISUALIZATION) {
        setFileTooLarge(true);
        setKeywordsJsonContent(null);
        setIsLoadingJson(false);
        return;
      }
      
      setIsLoadingJson(true);
      setFileTooLarge(false);
      
      const fetchKeywordsContent = async () => {
        try {
          if (!resultsUrl) {
            setIsLoadingJson(false);
            return;
          }
          const url = new URL(resultsUrl);
          const blobPath = url.pathname.substring(1); // Remove leading slash
          
          // Fetch the content
          const content = await blobService.getFileContent(blobPath);
          
          // Check JSON file size (second checkpoint)
          const contentSizeInMB = (content.length * 2) / (1024 * 1024); // Rough estimate for UTF-16
          const MAX_JSON_SIZE_MB = 30; // 30MB limit for JSON processing
          
          if (contentSizeInMB > MAX_JSON_SIZE_MB) {

            setFileTooLarge(true);
            setKeywordsJsonContent(null);
            setIsLoadingJson(false);
            return;
          }
          
          // Content size is acceptable, proceed with visualization
          setKeywordsJsonContent(content);
        } catch (error) {
          // Handle error silently - visualization will show appropriate message
        } finally {
          setIsLoadingJson(false);
        }
      };
      
      fetchKeywordsContent();
    }
  }, [status, status?.output_links, status?.status, status?.total_items]);

  // Frontend Snowflake save has been removed since backend now handles it automatically
  // The backend saves to Snowflake immediately after job completion to ensure
  // data is persisted even if frontend has memory issues with large files

  // Render configuration details if available
  const renderConfigDetails = () => {
    // Only show configuration details if:
    // 1. We're using database approach
    // 2. We have an active module ID
    // 3. The query succeeded (has data)
    if (!isUsingDatabaseApproach || !activeModuleId) {
      return null;
    }

    // If there's any error, don't show the configuration section at all
    if (moduleConfigQuery.isError) {
      return null;
    }

    // Show loading state
    if (moduleConfigQuery.isLoading) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loading configuration details...
        </Alert>
      );
    }

    // Show configuration panel if we have data
    if (moduleConfigQuery.data) {
      return <ConfigurationDetailsPanel configData={moduleConfigQuery.data} />;
    }

    return null;
  };

  // Render main content based on loading and status
  const renderMainContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!status) {
      return (
        <Alert severity="warning" sx={{ m: 2 }}>
          No status found for job {jobId}
        </Alert>
      );
    }

    return (
      <>
        {/* Render configuration details */}
        {renderConfigDetails()}
        
        {/* Real-time progress display for processing and completed jobs */}
        {(status.status === 'processing' && !isCompletedRef.current) || 
         (status.status === 'completed' || status.status === 'finalized') ? (
          <BatchJobProgressBar 
            status={activeStatusQuery.data || batchStatusDbQuery.data || legacyBatchStatusDbQuery.data || status}
          />
        ) : null}

        {/* Use new JobStatusTransition component for smooth state management */}
        <JobStatusTransition
          status={status}
          jobId={jobId}
          keywordsJsonContent={keywordsJsonContent}
          isLoadingJson={isLoadingJson}
          isSavingToSnowflake={isSavingToSnowflake}
          snowflakeSaveSuccess={snowflakeSaveSuccess}
          snowflakeSaveError={snowflakeSaveError}
          fileTooLarge={fileTooLarge}
          keywordCount={keywordCount}
        />

        {/* Download button for completed results - integrated into JobStatusTransition */}
        {(status?.output_links?.['results.json'] || status?.output_links?.categorized_keywords) && 
         status.status === 'completed' && keywordsJsonContent && (
          <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Box display="flex" justifyContent="center">
              <MuiButton
                variant="contained"
                startIcon={<DownloadIcon />}
                href={convertToApiDownloadUrl(status.output_links['results.json'] || status.output_links.categorized_keywords)}
                download={getFilenameFromUrl(status.output_links['results.json'] || status.output_links.categorized_keywords || '')}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
                size="large"
              >
                Download Complete Results
              </MuiButton>
            </Box>
          </Paper>
        )}
      </>
    );
  };

  return (
    <Box className="container mx-auto py-6">
      {/* Add Grid container to hold the stepper and content */}
      <Grid 
        container 
        spacing={2} 
        columnGap={!isStepperCollapsed ? 4 : 0}
        direction="row"
        wrap="nowrap"
        sx={{ display: 'flex' }}
      >
        {/* Add WorkflowStepper */}
        <Grid item sx={{ flex: '0 0 auto' }}>
          <WorkflowStepper
            activeStep={4}  // Set as Module 5 (index 4 in 0-based steps)
            workflowId={workflowIdStr || ''}
            clientId={clientIdStr || ''}
            moduleId={activeModuleId || (parsedJobId.numericId ?? undefined)}
            moduleCompletionStatus={getWorkflowStatus(status?.workflow_id)}
            onCollapseChange={handleStepperCollapseChange}
          />
        </Grid>
        
        {/* Main content in a Grid item */}
        <Grid 
          item 
          sx={{
            flex: '1 1 auto',
            pl: isStepperCollapsed ? 1 : 3,  // More left padding when stepper is open
            overflow: 'auto' // Ensure content can scroll if needed
          }}
        >
          {/* Breadcrumb navigation with back button */}
          {clientIdStr && workflowIdStr && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Breadcrumbs 
                aria-label="breadcrumb" 
                sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
              >
                <Link 
                  color="inherit" 
                  href="/" 
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Dashboard
                </Link>
                <Typography color="text.secondary">
                  {clientData?.name || `Client ${clientIdStr}`} ({clientIdStr} - {workflowIdStr})
                </Typography>
                <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <PlayArrowIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  Job Monitoring
                </Typography>
              </Breadcrumbs>
              
              <MuiButton
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/start-process')}
                sx={{ ml: 2 }}
              >
                Start Process
              </MuiButton>
            </Box>
          )}
          {renderMainContent()}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Module5JobMonitoring;
